import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["loading", "list", "empty", "tab", "composeModal", "composeModalContent", "composeToEmail", "composeCcField", "composeCcWrapper", "composeCcToggle", "composeSubject", "composeBody", "composeSendBtn", "logModal", "logModalContent", "logDirection", "logSubject", "logBody", "logSentAt", "logSaveBtn"]
  static values = { clientId: String, onboardingTemplateId: String }

  connect() {
    this.loaded = false
    // Set default log email datetime to now
    if (this.hasLogSentAtTarget) {
      const now = new Date()
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
      this.logSentAtTarget.value = now.toISOString().slice(0, 16)
    }
  }

  load() {
    if (this.loaded) return
    this.loaded = true

    this.loadingTarget.classList.remove("hidden")
    this.listTarget.classList.add("hidden")
    this.emptyTarget.classList.add("hidden")

    fetch(`/admin/email_inbox/client/${this.clientIdValue}`, {
      headers: {
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        this.renderEmails(data)
      })
      .catch(error => {
        console.error("Error loading emails:", error)
        this.loadingTarget.innerHTML = `<p class="text-sm text-[#E57373]">Failed to load emails. Please try again.</p>`
      })
  }

  renderEmails(emails) {
    this.loadingTarget.classList.add("hidden")

    if (emails.length === 0) {
      this.emptyTarget.classList.remove("hidden")
      return
    }

    this.listTarget.innerHTML = ""
    this.listTarget.classList.remove("hidden")

    emails.forEach(email => {
      const emailCard = document.createElement("div")
      emailCard.className = "mc-card p-4 hover:bg-[#F5F3F0] transition-colors cursor-pointer"
      emailCard.dataset.action = "click->client-emails#toggleEmail"
      emailCard.dataset.emailId = email.id

      const directionBadge = email.direction === "sent"
        ? `<span class="px-2 py-0.5 text-xs rounded-full bg-[#C4A484]/20 text-[#C4A484]">Sent</span>`
        : `<span class="px-2 py-0.5 text-xs rounded-full bg-[#8BA4B4]/20 text-[#8BA4B4]">Received</span>`

      const syncBadge = email.sync_source === "bmt_send"
        ? `<span class="px-2 py-0.5 text-xs rounded-full bg-[#9CAF88]/20 text-[#9CAF88]">Sent from SMT</span>`
        : email.sync_source === "manual"
        ? `<span class="px-2 py-0.5 text-xs rounded-full bg-[#8C8C8C]/20 text-[#8C8C8C]">Manual Log</span>`
        : `<span class="px-2 py-0.5 text-xs rounded-full bg-[#8C8C8C]/20 text-[#8C8C8C]">Gmail Sync</span>`

      const threadLink = email.thread_count > 1
        ? `<a href="/admin/email_inbox/${email.id}/thread" class="text-xs text-[#C4A484] hover:underline ml-2" onclick="event.stopPropagation()">View Thread (${email.thread_count})</a>`
        : ""

      emailCard.innerHTML = `
        <div class="flex items-start justify-between mb-2">
          <div class="flex items-center space-x-2">
            ${directionBadge}
            ${syncBadge}
            <span class="text-xs text-[#8C8C8C]">${new Date(email.sent_at).toLocaleString()}</span>
          </div>
          <div class="flex items-center space-x-2">
            ${email.open_count > 0 ? `<span class="text-xs text-[#8C8C8C]">${email.open_count} opens</span>` : ""}
            ${email.click_count > 0 ? `<span class="text-xs text-[#8C8C8C]">${email.click_count} clicks</span>` : ""}
          </div>
        </div>
        <div class="mb-1">
          <span class="text-sm font-medium text-[#2D2D2D]">${this.escapeHtml(email.subject || "(no subject)")}</span>
          ${threadLink}
        </div>
        <div class="text-sm text-[#8C8C8C] mb-2">
          <span class="font-medium">${email.direction === "sent" ? "To" : "From"}:</span> ${this.escapeHtml(email.direction === "sent" ? email.to_name : email.from_name)}
        </div>
        <div class="text-sm text-[#8C8C8C] line-clamp-2" data-email-snippet>
          ${this.escapeHtml(email.snippet || "")}
        </div>
        <div class="hidden mt-3 pt-3 border-t border-[#E8E4DF]" data-email-body>
          <div class="text-sm text-[#2D2D2D] prose prose-sm max-w-none">
            ${email.body_text || "<p class='text-[#8C8C8C] italic'>No body available</p>"}
          </div>
        </div>
      `

      this.listTarget.appendChild(emailCard)
    })
  }

  toggleEmail(event) {
    const emailCard = event.currentTarget
    const body = emailCard.querySelector("[data-email-body]")
    const snippet = emailCard.querySelector("[data-email-snippet]")

    if (body.classList.contains("hidden")) {
      body.classList.remove("hidden")
      snippet.classList.add("hidden")
    } else {
      body.classList.add("hidden")
      snippet.classList.remove("hidden")
    }
  }

  showComposeModal() {
    this.composeModalTarget.classList.remove("hidden")
    // Reset CC to hidden state
    if (this.hasComposeCcWrapperTarget) {
      this.composeCcWrapperTarget.classList.add("hidden")
      this.composeCcToggleTarget.classList.remove("hidden")
      this.composeCcFieldTarget.value = ""
    }
  }

  toggleCc() {
    this.composeCcWrapperTarget.classList.toggle("hidden")
    this.composeCcToggleTarget.classList.toggle("hidden")
    if (!this.composeCcWrapperTarget.classList.contains("hidden")) {
      this.composeCcFieldTarget.focus()
    }
  }

  showOnboardingEmail() {
    this.composeModalTarget.classList.remove("hidden")
    this.composeSubjectTarget.value = "Welcome to TREND! Let's get started"

    // Show CC field pre-populated with ash@trendpr.com
    if (this.hasComposeCcWrapperTarget) {
      this.composeCcWrapperTarget.classList.remove("hidden")
      this.composeCcToggleTarget.classList.add("hidden")
      this.composeCcFieldTarget.value = "ash@trendpr.com"
    }

    const templateId = this.onboardingTemplateIdValue
    if (!templateId) return

    // Ensure client has an onboarding token, then resolve template
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(`/admin/clients/${this.clientIdValue}/generate_onboarding_token`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      }
    })
    .then(response => response.json())
    .then((data) => {
      // Surface a portal-provisioning warning here rather than let it linger
      // onto a later page (re-review finding 3).
      if (data && data.alert) window.alert(data.alert)
      // Now resolve the template (token is guaranteed to exist)
      const url = `/admin/email_templates/${templateId}/resolve?client_id=${encodeURIComponent(this.clientIdValue)}`
      return fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.body) {
        this.composeBodyTarget.innerHTML = data.body
      }
    })
    .catch(error => {
      console.error("Failed to load onboarding template:", error)
    })
  }

  closeComposeModal() {
    this.composeModalTarget.classList.add("hidden")
    this.composeSubjectTarget.value = ""
    this.composeBodyTarget.innerHTML = ""
    if (this.hasComposeCcFieldTarget) {
      this.composeCcFieldTarget.value = ""
    }
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      if (this.hasComposeModalTarget && event.currentTarget === this.composeModalTarget) {
        this.closeComposeModal()
      }
      if (this.hasLogModalTarget && event.currentTarget === this.logModalTarget) {
        this.closeLogModal()
      }
    }
  }

  sendEmail(event) {
    event.preventDefault()

    if (!this.composeToEmailTarget.value.trim()) {
      alert("No email on file for this client. Please enter a recipient email address.")
      return
    }

    const bodyHtml = this.composeBodyTarget.innerHTML.trim()
    if (!bodyHtml || bodyHtml === "<br>" || bodyHtml === "<div><br></div>") {
      alert("Please enter a message body.")
      return
    }

    const formData = new FormData()
    formData.append("to_email", this.composeToEmailTarget.value)
    formData.append("to_name", "")
    formData.append("subject", this.composeSubjectTarget.value)
    formData.append("body_html", bodyHtml)

    // Include CC if present
    if (this.hasComposeCcFieldTarget) {
      const cc = this.composeCcFieldTarget.value.trim()
      if (cc) {
        formData.append("cc", cc)
      }
    }

    this.composeSendBtnTarget.disabled = true
    this.composeSendBtnTarget.textContent = "Sending..."

    fetch("/admin/email_inbox/compose", {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.closeComposeModal()
          this.loaded = false
          this.load()
        } else {
          alert(data.message || "Failed to send email")
        }
        this.composeSendBtnTarget.disabled = false
        this.composeSendBtnTarget.textContent = "Send Email"
      })
      .catch(error => {
        console.error("Error sending email:", error)
        alert("Failed to send email. Please try again.")
        this.composeSendBtnTarget.disabled = false
        this.composeSendBtnTarget.textContent = "Send Email"
      })
  }

  showLogModal() {
    this.logModalTarget.classList.remove("hidden")
  }

  closeLogModal() {
    this.logModalTarget.classList.add("hidden")
    this.logSubjectTarget.value = ""
    this.logBodyTarget.value = ""
  }

  logEmail(event) {
    event.preventDefault()

    const formData = new FormData()
    formData.append("shared_client_id", this.clientIdValue)
    formData.append("direction", this.logDirectionTarget.value)
    formData.append("subject", this.logSubjectTarget.value)
    formData.append("body_text", this.logBodyTarget.value)
    formData.append("sent_at", this.logSentAtTarget.value)

    this.logSaveBtnTarget.disabled = true
    this.logSaveBtnTarget.textContent = "Logging..."

    fetch("/admin/email_inbox/log_email", {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.closeLogModal()
          this.loaded = false
          this.load()
        } else {
          alert(data.message || "Failed to log email")
        }
        this.logSaveBtnTarget.disabled = false
        this.logSaveBtnTarget.textContent = "Log Email"
      })
      .catch(error => {
        console.error("Error logging email:", error)
        alert("Failed to log email. Please try again.")
        this.logSaveBtnTarget.disabled = false
        this.logSaveBtnTarget.textContent = "Log Email"
      })
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
